import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import AUIIcon from '@ancert/aui-icon';
import { format, parseISO, parse, isValid } from 'date-fns';
import { ReactComponent as LogoConsejo } from '../../../../assets/images/CGNColor.svg';
import { ReactComponent as LogoCTN } from '../../../../assets/images/CTNColor.svg';

/**
 * Formatea una fecha, verificando primero si ya está en el formato esperado
 * @param {string} fecha - Fecha en formato ISO o ya formateada
 * @returns {string} Fecha formateada como 'dd/MM/yyyy HH:mm'
 */
const formatearFecha = (fecha) => {
  const formatoEsperado = 'dd/MM/yyyy HH:mm';
  const fechaParsedaConFormato = parse(fecha, formatoEsperado, new Date());
  if (isValid(fechaParsedaConFormato)) {
    return fecha;
  }
  return format(parseISO(fecha), formatoEsperado);
};

/**
 * Componente que contiene el contenido principal del modal de validación de firma
 * @param {Object} props - Props del componente
 * @param {Object} [props.validationData] - Datos de validación de la firma
 * @param {string} [props.validationData.nombreFirmante] - Nombre del firmante
 * @param {string} [props.validationData.fechaFirma] - Fecha de la firma
 * @param {string} [props.validationData.fechaValidacion] - Fecha de validación
 * @param {boolean} [props.validationData.estadoFirma] - Estado de la firma
 * @param {boolean} [props.validationData.contenidoAlterado] - Si el contenido fue alterado
 * @param {boolean} [props.validationData.certificadoCaducado] - Si el certificado está caducado
 * @param {boolean} [props.validationData.certificadoRevocado] - Si el certificado está revocado
 * @param {boolean} [props.validationData.certificadoCa] - Si el certificado es de CA confiable
 * @returns {React.ReactElement}
 */
const ValidationContent = ({
  validationData = {
    nombreFirmante: "Nombre y apellidos del notario",
    fechaFirma: "23/01/2024 10:31",
    fechaValidacion: "24/01/2024 10:31",
    estadoFirma: true,
    contenidoAlterado: false,
    certificadoCaducado: false,
    certificadoRevocado: false,
    certificadoCa: true
  }
} = {}) => {
  const {
    nombreFirmante,
    fechaFirma,
    fechaValidacion,
    estadoFirma,
    contenidoAlterado,
    certificadoCaducado,
    certificadoRevocado,
    certificadoCa
  } = validationData;

  const fechaFirmaFormateada = formatearFecha(fechaFirma);
  const fechaValidacionFormateada = formatearFecha(fechaValidacion);
  const { t } = useTranslation();

  return (
  <div className="modal-validation-content">
    {/* Caja principal con bordes redondeados */}
    <div className="validation-main-box">
      {/* Información de la firma en 4 columnas */}
      <div className="validation-info-grid">
        <div className="validation-info-item">
          <div className="validation-info-label">{t('modalValidacionFirma.firmaNotario')}</div>
          <div className="validation-info-value">{nombreFirmante}</div>
        </div>
        <div className="validation-info-item">
          <div className="validation-info-label">{t('modalValidacionFirma.estadoFirma')}</div>
          <div className="validation-info-value validation-status-valid">
            <AUIIcon icon="okCircle" color="green" />
            {estadoFirma ? t('modalValidacionFirma.validada') : t('modalValidacionFirma.noValidada')}
          </div>
        </div>
        <div className="validation-info-item">
          <div className="validation-info-label">{t('modalValidacionFirma.fechaFirma')}</div>
          <div className="validation-info-value">{fechaFirmaFormateada}</div>
        </div>
        <div className="validation-info-item">
          <div className="validation-info-label">{t('modalValidacionFirma.fechaValidacion')}</div>
          <div className="validation-info-value">{fechaValidacionFormateada}</div>
        </div>
      </div>

      {/* Sección de validación con borde superior gris */}
      <div className="validation-details-section">
        <div className="validation-details-content">
          <div className="validation-details-left">
            <h3 className="validation-details-title">
              {t('modalValidacionFirma.validacionFirma')}
            </h3>
            <ul className="validation-checks-list">
              <li className="validation-check-item">
                <AUIIcon
                  icon={contenidoAlterado ? "errorCircle" : "okCircle"}
                  color={contenidoAlterado ? "red" : "green"}
                />
                {t('modalValidacionFirma.contenido')} {contenidoAlterado ? t('modalValidacionFirma.alterado') : t('modalValidacionFirma.noAlterado')}
              </li>
              <li className="validation-check-item">
                <AUIIcon
                  icon={certificadoCaducado ? "errorCircle" : "okCircle"}
                  color={certificadoCaducado ? "red" : "green"}
                />
                {t('modalValidacionFirma.certificadoFirmante')} {certificadoCaducado ? t('modalValidacionFirma.caducado') : t('modalValidacionFirma.noCaducado')}
              </li>
              <li className="validation-check-item">
                <AUIIcon
                  icon={certificadoRevocado ? "errorCircle" : "okCircle"}
                  color={certificadoRevocado ? "red" : "green"}
                />
                {t('modalValidacionFirma.certificadoFirmante')} {certificadoRevocado ? t('modalValidacionFirma.revocado') : t('modalValidacionFirma.noRevocado')}
              </li>
              <li className="validation-check-item">
                <AUIIcon
                  icon={certificadoCa ? "okCircle" : "errorCircle"}
                  color={certificadoCa ? "green" : "red"}
                />
                {t('modalValidacionFirma.certificadoFirmante')} {certificadoCa ? t('modalValidacionFirma.certificadoCa') : t('modalValidacionFirma.noCertificadoCa')}
              </li>
            </ul>
          </div>
          <div className="validation-details-right">
            <div className="validation-logos">
              <LogoConsejo className="validation-logo" />
              <LogoCTN className="validation-logo" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

ValidationContent.propTypes = {
  validationData: PropTypes.shape({
    nombreFirmante: PropTypes.string,
    fechaFirma: PropTypes.string,
    fechaValidacion: PropTypes.string,
    estadoFirma: PropTypes.bool,
    contenidoAlterado: PropTypes.bool,
    certificadoCaducado: PropTypes.bool,
    certificadoRevocado: PropTypes.bool,
    certificadoCa: PropTypes.bool,
  }),
};

ValidationContent.defaultProps = {
  validationData: {
    nombreFirmante: "Nombre y apellidos del notario",
    fechaFirma: "23/01/2024 10:31",
    fechaValidacion: "24/01/2024 10:31",
    estadoFirma: true,
    contenidoAlterado: false,
    certificadoCaducado: false,
    certificadoRevocado: false,
    certificadoCa: true,
  },
};

export default ValidationContent;
